import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import FloorplanCanvas from './FloorplanCanvas3';
import RoomUsePanel from './RoomUsePanel4';
import { GREEN, CHARCOAL, MUTED_FAINT, BORDER, STONE50 } from '../../../theme/tokens2';

// ─── CombinationWorkspace ───────────────────────────────────────────────────
// The "you're inside this arrangement" view. Split into two columns:
//   - the floorplan canvas, a flexible column that absorbs whatever width
//     isn't claimed by the rail (so a wide apartment gets real room to
//     breathe, and FloorplanCanvas's own zoom/pan covers the rest)
//   - a fixed-width rail on the right, governed by a single RAIL-LEVEL
//     tab pair -- "Explore" / "Notes". Explore holds the room-use
//     browser (RoomUsePanel); Notes gets its own tab and, with it, the
//     ENTIRE rail height to write in, rather than a fixed ~128px band
//     pinned below everything else.
//
// Why this shape and not a full-workspace tab split (canvas+RoomUse vs.
// Notes): Notes here is a running scratchpad ("jot down thoughts as you
// go"), not a separate long-form document -- it's written WHILE looking
// at the floorplan, not instead of it. Keeping the canvas permanently
// visible and only switching the rail's own content avoids forcing the
// user to hold a room's layout in memory while they swap away from it
// to write about it, and back again to check something.
//
// On narrow viewports the rail drops below the canvas and the whole page
// scrolls as one column -- height isn't scarce there the way it is in a
// fixed side-by-side rail, so no special internal-scroll behaviour is
// needed at that breakpoint.
//
// Props:
//   combination         — a RoomCombinationDto-shaped object
//   furniturePieces      — the global FurniturePieceDto[] catalog, threaded
//                           through to FloorplanCanvas and RoomUsePanel
//   pxPerMm              — project-wide mm-to-px scale factor, threaded
//                           through to RoomUsePanel (-> LayoutThumb) for
//                           sizing room thumbnails by their real dimensions
//   onSelectLayout       — fn(roomLabel, roomUseName, layoutOptionId)
//   onSaveNotes          — fn(text)

const RAIL_WIDTH = 340; // a fixed floor, not a flex ratio -- keeps the rail
                         // usable even when the dashboard's own 220px sidebar
                         // eats into the available width on smaller screens

export default function CombinationWorkspace({
  combination,
  furniturePieces,
  pxPerMm,
  onSelectLayout,
  onSaveNotes,
}) {
  const [selectedRoomLabel, setSelectedRoomLabel] = useState(null);
  const [railTab, setRailTab] = useState('explore'); // 'explore' | 'notes'

  const selectedRoom = combination.rooms.find((r) => r.label === selectedRoomLabel) || null;

  return (
    <Box sx={{
      background: '#fff', border: `1.5px solid rgba(0,0,0,0.14)`, borderRadius: '4px', overflow: 'hidden',
      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
    }}>

      {/* Canvas -- flexible column. Plain block layout deliberately, not a
          flex container: a flex item's width resolves through flex-basis,
          which is one more layer of CSS that can go sideways depending on
          the parent's exact settings. A block-level child just fills its
          parent's width unconditionally -- the one thing we actually need
          guaranteed here. */}
      <Box sx={{ flex: 1, minWidth: 0, padding: '20px' }}>
        <FloorplanCanvas
          combination={combination}
          furniturePieces={furniturePieces}
          selectedRoomLabel={selectedRoomLabel}
          onRoomClick={setSelectedRoomLabel}
          interactive
          maxHeight={{ xs: 420, md: 600 }}
        />
      </Box>

      {/* Rail -- a real fixed HEIGHT on md+ (not just a cap), so whichever
          tab is active (Explore's RoomUsePanel column, or Notes's
          full-height textarea) has actual space to use, instead of the
          whole workspace shrinking to fit a one-line placeholder when no
          room is selected. */}
      <Box sx={{
        width: { xs: '100%', md: RAIL_WIDTH },
        flexShrink: 0,
        borderTop: { xs: `1px solid ${BORDER}`, md: 'none' },
        borderLeft: { xs: 'none', md: `1px solid ${BORDER}` },
        display: 'flex', flexDirection: 'column',
        height: { xs: 'auto', md: 640 },
      }}>

        {/* Rail-level tabs -- the ONE tab pair governing the whole rail. */}
        <Box sx={{ display: 'flex', gap: '18px', padding: '0 24px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <TabButton label="Explore" isActive={railTab === 'explore'} onClick={() => setRailTab('explore')} />
          <TabButton label="Notes" isActive={railTab === 'notes'} onClick={() => setRailTab('notes')} />
        </Box>

        {/* Rail content. For Explore, this IS the scroll container --
            RoomUsePanel has no scroll mechanism of its own, so it
            scrolls here if it runs long, and nothing gets chopped off.
            For Notes, scrolling is deliberately
            left to the textarea itself (see NotesTab): the textarea
            already fills this box exactly and scrolls internally on its
            own once its content overflows, so this outer box is set to
            'hidden' rather than 'auto' -- giving it 'auto' too would (a)
            be redundant with the textarea's own scrollbar, and (b) per
            CSS's overflow-axis-pairing rule, force overflow-x to compute
            as 'auto' as well since only overflow-y was set, producing an
            unwanted extra/horizontal scrollbar on the outer box. */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: { xs: 'visible', md: railTab === 'notes' ? 'hidden' : 'auto' } }}>
          {railTab === 'explore' ? (
            <ExploreTab
              selectedRoom={selectedRoom}
              furniturePieces={furniturePieces}
              pxPerMm={pxPerMm}
              onSelectLayout={(roomUseName, layoutOptionId) =>
                onSelectLayout(selectedRoom.label, roomUseName, layoutOptionId)
              }
            />
          ) : (
            <NotesTab notes={combination.notes} onSaveNotes={onSaveNotes} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Explore tab ─────────────────────────────────────────────────────────
// Just the room-use browser, in the same scrollable column the parent
// Box above owns -- a room with many uses/layouts scrolls as one
// continuous rail, never clipping.
function ExploreTab({ selectedRoom, furniturePieces, pxPerMm, onSelectLayout }) {
  return (
    <Box sx={{ padding: '20px 24px 20px' }}>
      {selectedRoom ? (
        <RoomUsePanel
          key={selectedRoom.label}
          room={selectedRoom}
          furniturePieces={furniturePieces}
          pxPerMm={pxPerMm}
          onSelectLayout={onSelectLayout}
        />
      ) : (
        <Typography sx={{ fontSize: '13px', color: 'rgba(0,0,0,0.65)' }}>
          Click a room on the plan to try out its layouts.
        </Typography>
      )}
    </Box>
  );
}

// ─── Notes tab ───────────────────────────────────────────────────────────
// Gets the entire rail height now, not a fixed ~128px band pinned under
// RoomUsePanel -- a real scratchpad instead of a cramped afterthought.
// No manual resize handle: since the textarea already fills all available
// rail height by default, a resize handle would only ever shrink it.
function NotesTab({ notes, onSaveNotes }) {
  const [draft, setDraft] = useState(notes || '');

  function handleBlur() {
    if (draft !== notes) onSaveNotes(draft);
  }

  return (
    <Box sx={{ boxSizing: 'border-box', height: '100%', minHeight: 300, padding: '10px 12px', display: 'flex', flexDirection: 'column' }}>
      <Box
        component="textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        placeholder="Jot down thoughts as you go..."
        sx={{
          boxSizing: 'border-box',
          flex: 1, minHeight: 0, width: '100%', fontSize: '13.5px', lineHeight: 1.5, color: CHARCOAL,
          background: STONE50, border: `1px solid ${BORDER}`, borderRadius: '8px',
          padding: '10px 12px', resize: 'none', fontFamily: 'inherit',
          '&::placeholder': { color: MUTED_FAINT },
          '&:focus': { outline: 'none', borderColor: 'rgba(33,131,51,0.30)' },
        }}
      />
    </Box>
  );
}

function TabButton({ label, isActive, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '10px 0 8px',
        fontSize: '11px', fontWeight: isActive ? 600 : 500,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        color: isActive ? GREEN : 'rgba(0,0,0,0.45)',
        borderBottom: isActive ? `2px solid ${GREEN}` : '2px solid transparent',
      }}
    >
      {label}
    </Box>
  );
}